import axios from 'axios'
import { defineStore } from 'pinia'
import { format, addDays } from 'date-fns'
import { formatDateDMY } from '@/utils/date'
import { useUsersStore } from '@/stores/users'
import { notify } from '@kyvg/vue3-notification'
import { maintainerData } from '@/composables/useLayouts'
import isBoolean from 'lodash-es/isBoolean'

export function useSignature() {

    return defineStore('signatures', {
        state: () => ({
            loading: {
                data: false,
                invoices: false,
                clients: false,
                signature: false,
                cancel: false,
                plans: false,
            },
            signature: {},
            signatureChat: {},
            signatureStatus: {},
            client: {},
            address: {},
            invoices: [],
            confirm: {
                step: 1,
            },
            plans: [],
        }),

        getters: {
            clientDocument() {
                const doc = this.client?.document
                if (!doc) return ''

                if (doc.length === 11) {
                    this.client.document = doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
                    return this.client.document
                }

                this.client.document = doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
                return this.client.document
            },

            alertSignature() {
                const signature = this.signatureStatus
                if (!signature || !signature.show_alert) return []

                const user = useUsersStore().user

                // TODO: Review validation, when specific modules are allowed
                if (user?.level < 2) return []

                let date = format(addDays(new Date(), signature.due_in), 'dd/MM/yyyy')
                let content
                switch (signature.status) {
                    case 'active':
                        content = 'Abaixo, pode visualizar todas as suas faturas. Em caso de quaisquer dúvidas, ' +
                            'não hesite em entrar em contato com o suporte, pelo número ' +
                            `<b>${maintainerData.value.support_phone}</b> ou pelo e-mail <b>${maintainerData.value.support_email}</b>. ` +
                            'Em caso de qualquer divergência, evite bloqueios: acione o suporte e comunique.<br /><br />'

                        if (signature.due_in === 0) {
                            content += '<b>HOJE É SEU ÚLTIMO DIA DE ACESSO!<br /><br /> Sugerimos que entre em contato ' +
                                'com o suporte para verificar a sua assinatura o mais breve possível.</b>'
                        } else {
                            content += `<b>Sua assinatura expira em ${date}. Ainda tem ${signature.due_in} dias de acesso!</b>`
                        }

                        return [
                            'Podem existir pendências financeiras em sua assinatura!',
                            content,
                            'error',
                        ]

                    case 'trying':
                        content = 'Ao se cadastrar, ganhou alguns dias de acesso para testar o sistema e todas as ' +
                            'funcionalidades, para melhorar o atendimento aos seus clientes. Mas agora, chegou a hora de ' +
                            'decidir: deseja continuar com a assinatura ativa?<br /><br />'

                        if (signature.due_in === 0) {
                            content += '<b>HOJE É SEU ÚLTIMO DIA PARA APROVEITAR O SISTEMA!</b>'
                        } else {
                            content += `<b>Você pode usar o sistema até ${date}, por mais ${signature.due_in} dias!</b>`
                        }

                        return [
                            'Seu período de teste está chegando ao fim...',
                            content,
                            'warning',
                            [
                                {
                                    router: 'auth.confirm_signature',
                                    content: 'Clique aqui para confirmar a assinatura e continuar usando o sistema',
                                },
                            ],
                        ]
                }

                return []
            },

            bannerAlertSignature() {
                const signature = this.signatureStatus
                if (!signature || !signature.show_alert) return []

                let alertText = ''
                if (signature.due_in === 0) {
                    alertText = '<b>HOJE É SEU ÚLTIMO DIA DE ACESSO!</b> '
                } else {
                    alertText = '<b>ATENÇÃO:<b/> '
                }

                switch (signature.status) {
                    case 'active':
                        return alertText + 'Sua assinatura precisa ser verificada. '

                    case 'trying':
                        return alertText + 'Seu período de teste está se encerrando. '
                }

                return []
            },

            feedbackSignature() {
                if (!this.signatureStatus) return []

                switch (this.signatureStatus.status) {
                    case 'active':
                        if (this.signatureStatus.due_in <= 3) {
                            const supportLink = 'https://wa.me/554331421104?text=Preciso+falar+com+o+financeiro+sobre+minha+assinatura&type=phone_number&app_absent=0'
                            return [
                                'Podem exister pendências!',
                                '<b>A sua assinatura ainda está ativa!</b> Contudo, podem existir pendências ' +
                                'que irão bloquear sua assinatura. Abaixo, você pode visualizar seu histórico financeiro. ' +
                                'Em caso de dúvidas, não hesite em acionar o suporte ' +
                                `<b><a title="Falar com suporte" href="${supportLink}" target="_blank">clicando aqui.</a></b><br /><br />` +
                                '<b>O período de sua assinatura se encerrou em ' +
                                formatDateDMY(this.signature.due_date) + '.</b>',
                                'error',
                            ]
                        }

                        return [
                            'Sua assinatura está ativa!',
                            'Abaixo, você pode atualizar seus dados de cobrança, visualizar suas faturas e suas notas. ' +
                            'Sua assinatura é renovada automaticamente e <b>período atual se encerra em ' +
                            formatDateDMY(this.signature.due_date) + '.</b>',
                            'success',
                        ]

                    case 'trying':
                        return [
                            'Você está no período de teste!',
                            'Ao se cadastrar, você ganha alguns dias de acesso para testar o sistema e todas as suas ' +
                            'funcionalidades, para melhorar o atendimento aos seus clientes. <br /><b>Seu período de ' +
                            'teste se encerra em ' + formatDateDMY(this.signature.due_date) + '.</b>',
                            'warning',
                            [
                                {
                                    content: 'CLIQUE AQUI PARA CONFIRMAR SUA ASSINATURA!',
                                    router: 'auth.confirm_signature',
                                },
                            ],
                        ]

                    case 'to_block':
                        return [
                            'Aaah, que pena 😢',
                            'Sentimos muito por você estar nos deixando. Estaremos sempre aqui, prontos para lhe ' +
                            'atender!<br /><br />' +
                            '<b>Você poderá utilizar o sistema até ' + formatDateDMY(this.signature.due_date) + '.</b>',
                            'warning',
                        ]
                }

                return []
            },

            getPlan() {
                return this.signatureChat.plan ?? {}
            },

            getFeatureIa(){
                return (this.signatureChat.features?.includes('agent_ia') || this.signatureChat.features?.includes('ia_pln')) ?? false
            },

            getFetureById: (state) => (id) =>  {
                return state.signatureChat.features.includes(id) ?? false
            },

            getFetures() {
                return this.signatureChat.features ?? []
            },

            getPlanLimits() {
                return this.signatureChat?.plan?.limits || {}
            },

            /** @returns {boolean} */
            accessAutomation() {
                const type = this.getPlan?.type
                if (!type) return true

                const accessAutomation = ['automation', 'only_automation']

                let access = false
                type.forEach(item => {
                    if (accessAutomation.includes(item)) access = true
                })

                return access
            },
        },

        actions: {
            getClient() {
                this.loading.clients = true

                axios.get('/v1/signatures/client')
                    .then(response => {
                        this.client = response.data.data.client
                        this.address = response.data.data.address
                    })
                    .catch(error => {
                        notify({
                            type: 'error',
                            title: 'Erro',
                            text: 'Não foi possível carregar os dados da assinatura.',
                        })

                        console.error(error)
                    })
                    .finally(() => this.loading.clients = false)
            },

            getSignature() {
                this.loading.signature = true

                axios.get('/v1/signatures')
                    .then(response => {
                        this.signature = response.data.data
                        const signatureStatus = response.data?.status
                        if (!signatureStatus) return
                        this.signatureStatus = signatureStatus
                    })
                    .catch(error => {
                        notify({
                            type: 'error',
                            title: 'Erro',
                            text: 'Não foi possível carregar os dados da assinatura.',
                        })

                        console.error(error)
                    })
                    .finally(() => this.loading.signature = false)
            },

            getInvoices() {
                this.loading.invoices = true

                axios.get('/v1/signatures/invoices')
                    .then(response => this.invoices = response.data)
                    .catch(error => console.log(error))
                    .finally(() => this.loading.invoices = false)
            },

            getPlans() {
                this.loading.plans = true

                axios.get('/v1/signatures/plans')
                    .then(response => {
                        const data = response.data.data

                        if (data.length === 0) {
                            notify({
                                type: 'error',
                                title: 'Erro',
                                text: 'Não foi possível carregar os planos disponíveis.',
                            })
                            return
                        }

                        this.plans = data
                    })
                    .catch(error => {
                        console.error(error)
                        notify({
                            type: 'error',
                            title: 'Erro',
                            text: 'Não foi possível carregar os planos disponíveis.',
                        })
                    })
                    .finally(() => this.loading.plans = false)
            },

            updateClient() {
                this.loading.clients = true

                const payload = { client: this.client, address: this.address }
                axios.post('/v1/signatures/client', payload)
                    .then(() => {
                        notify({
                            type: 'success',
                            title: 'Sucesso',
                            text: 'Dados atualizados com sucesso.',
                        })

                        this.confirm.step = 2
                    })
                    .catch(() => {
                        // TODO: Complement here...
                    })
                    .finally(() => this.loading.clients = false)
            },

            cancelSignature() {
                this.loading.cancel = true
                
                // Import router inside the action
                import('vue-router').then(({ useRouter }) => {
                    const router = useRouter()
                    
                    axios.delete('/v1/signatures/cancel')
                        .then(() => {
                            notify({
                                type: 'success',
                                title: 'Assinatura cancelada',
                                text: 'Conforme solicitação, a assinatura foi cancelada. Você poderá utilizar até o final do período contratado.',
                            })

                            setTimeout(() => {
                                router.push({ name: 'signature.home' }).then(r => r)
                                this.loading.cancel = false
                            }, 3000)
                        })
                        .catch(() => {
                            setTimeout(() => {
                                router.push({ name: 'signature.home' }).then(r => r)
                                this.loading.cancel = false
                            }, 3000)
                        })
                })
            },

            /**
             * @param {int} channelsRegistered
             * @returns {boolean}
             */
            checkLimitChannel(channelsRegistered) {
                const limitPlanChannel = this.signatureChat?.plan?.limits?.channels
                if (!limitPlanChannel) return false

                return channelsRegistered >= limitPlanChannel
            },

            /**
             * @param {int} usersRegistered
             * @returns {boolean}
             */
            checkLimitUsers(usersRegistered) {
                const limitPlanUsers = this.signatureChat?.plan?.limits?.users
                if (!limitPlanUsers) return false

                return usersRegistered >= limitPlanUsers
            },

            /**
             * @param {string} feature
             * @returns {boolean}
             */
            existFeature(feature) {
                const features = this.signatureChat?.features

                return features?.includes(feature)
            }
        },
    })
}
